/**
 * Delete Role Confirmation Modal
 * 
 * Confirmation dialog for deleting roles with safeguards.
 * Follows UI/UX guidelines for destructive actions.
 */

import { Modal, Button, Alert } from '@/components/ui';
import type { Role } from '@/services/RolesService';

interface DeleteRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  role: Role | null;
  isDeleting?: boolean;
}

export function DeleteRoleModal({
  isOpen,
  onClose,
  onConfirm,
  role,
  isDeleting = false,
}: DeleteRoleModalProps) {
  if (!role) return null;

  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Role"
      size="sm"
    >
      <div className="space-y-4">
        {/* Warning Alert */}
        <Alert variant="warning">
          <p className="text-sm">
            This action cannot be undone. Users with this role will lose their assigned permissions.
          </p>
        </Alert>

        {/* Role Info */}
        <div className="bg-muted rounded-lg p-4">
          <p className="text-sm text-muted-foreground">You are about to delete:</p>
          <p className="text-base font-semibold text-foreground mt-1">
            {role.name}
          </p>
          {role.description && (
            <p className="text-sm text-muted-foreground mt-1">{role.description}</p>
          )}
          <p className="text-sm text-muted-foreground mt-2">
            {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            isLoading={isDeleting}
          >
            Delete Role
          </Button>
        </div>
      </div>
    </Modal>
  );
}

